import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Layer } from '@deck.gl/core';
import { ArcLayer } from '@deck.gl/layers';
import { useMetaData } from '../../data/useMetaData';
import { useTripsData } from '../../data/useTripsData';
import { useAggregationConfig } from '../useAggregationConfig';
import { useMapColorPalette } from '../useMapColorPalette';
import { useMapVisibility } from '../useMapVisibility';
import { useCustomAreaCreation } from '../useCustomAreaCreation';
import { useCustomAreaEdition } from '../useCustomAreaEdition';
import type { Trip } from '../../../types/domain';
import type { Legend } from '../../../types/common';
import { alpha, toRgb, type RgbColor } from '../../../utils/color';
import { formatDuration, formatTime } from '../../../utils/format';

type ArcData = Trip & {
  tooltip: string;
};

type UseArcLayer = {
  arcLayer: Layer;
  arcLegends: Legend[];
};

const useArcLayer = (): UseArcLayer => {
  const { t } = useTranslation();
  const { currentDataMeta } = useMetaData();
  const { trips } = useTripsData();
  const { aggregationConfig } = useAggregationConfig();
  const { isAggregationVisible } = useMapVisibility();
  const { getRgbColors, mapColorOpacity } = useMapColorPalette();
  const { creatingCustomArea } = useCustomAreaCreation();
  const { editingCustomArea } = useCustomAreaEdition();

  const data = useMemo((): ArcData[] => {
    return trips.map((trip: Trip): ArcData => {
      return {
        ...trip,
        tooltip: [
          `${t('gender')}: ${trip.gender === '1' ? t('genders.male') : trip.gender === '2' ? t('genders.female') : '-'}`,
          `${t('ageGroup')}: ${trip.age ? (currentDataMeta?.type === 'gad_standard' && trip.age === '60' ? t(`ageGroups.60s+`) : t(`ageGroups.${trip.age}s`, { defaultValue: '-' })) : '-'}`,
          `${t('departureTime')}: ${trip.dep_time_seconds !== null ? formatTime(trip.dep_time_seconds) : '-'}`,
          `${t('longestDistanceMode')}: ${trip.longest_distance_mode ? t(`travelMode.${trip.longest_distance_mode}`, { defaultValue: '-' }) : '-'}`,
          `${t('traveledTime')}: ${formatDuration(trip.trav_time_seconds)}`,
        ].join('\n'),
      };
    });
  }, [trips, currentDataMeta?.type, t]);

  const legends = useMemo((): Legend[] => {
    const colors = getRgbColors(2).map((color: RgbColor): RgbColor => {
      return alpha(color, mapColorOpacity);
    });

    return [
      { range: [0, 0], color: colors[0], label: t('startPlace') },
      { range: [0, 0], color: colors[1], label: t('endPlace') },
    ];
  }, [getRgbColors, mapColorOpacity, t]);

  const arcLayer = new ArcLayer<ArcData>({
    id: 'arc-layer',
    data: data,
    getSourcePosition: (arc: ArcData): [number, number] => {
      return [arc.start_lon, arc.start_lat];
    },
    getTargetPosition: (arc: ArcData): [number, number] => {
      return [arc.end_lon, arc.end_lat];
    },
    getSourceColor: legends[0].color,
    getTargetColor: legends[1].color,
    getWidth: 2,
    widthUnits: 'pixels',
    widthMinPixels: 1,
    autoHighlight: !creatingCustomArea && !editingCustomArea,
    highlightColor: toRgb(alpha('#848C12', 0.6)),
    pickable: !creatingCustomArea && !editingCustomArea,
    visible: isAggregationVisible && aggregationConfig?.type === 'arcs',
  });

  return { arcLayer: arcLayer, arcLegends: legends };
};

export { useArcLayer };
